<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useAppStore } from '@/modules/app/stores/app.store'
import { useClientStore } from '@/modules/clients/stores/client.store'
import { useUserStore } from '@/modules/users/stores/user.store.ts'
import { useFilterStore } from '@/modules/filter/stores/filter.store'
import { useSearchStore } from '../stores/search.store'
import { useMotionStore } from '@/modules/motion/stores/motion.store'
import { useToaster } from '@/modules/toaster/composables/use-toaster'
import { ViewType } from '@/modules/clients/types/client.enums'
import { SCROLL_MOTION } from '@/constants'
import { ROUTE_NAMES } from '@/router/constants/route.constants'
import type { ClientView } from '@/api/models/types'
import type { ClientHit } from '../models/search.model'

import SearchInput from './SearchInput.vue'
import SearchMenu from './SearchMenu.vue'
import eventBus from '@/event-bus'
import router from '@/router'

const route = useRoute()
const appStore = useAppStore()
const userStore = useUserStore()
const clientStore = useClientStore()
const searchStore = useSearchStore()
const motionStore = useMotionStore()
const filterStore = useFilterStore()

const isSearchMenuOpen = ref<boolean>(false)
const isFocus = ref<boolean>(false)
const isLoading = ref<boolean>(false)
const clientHits = ref<ClientHit[]>([])
const selected = ref<ClientHit>()

const { warning } = useToaster()

const hasCurrentClient = computed((): boolean =>
  clientHits.value.some((client) => client.clientKey === userStore.user?.activeClient?.clientKey),
)

const isSearchDisabled = computed(() => !filterStore.tagContext?.search)

const scrollToTop = (): void => {
  eventBus.emit(SCROLL_MOTION)
}

const performSearch = async (): Promise<void> => {
  await checkCurrentRoute()
  await checkSelectedClient()
  await getMotionSelectedItems()
}

const findSelectedClient = (clients: ClientHit[]): ClientHit | undefined => {
  const activeClient = userStore.user?.activeClient
  const activeViewId = clientStore.activeViewId

  switch (activeViewId) {
    case ViewType.OutboundShipments:
    case ViewType.AgentShipments:
      return clients.find((client) => client.clientKey === activeClient?.clientKey)
    case ViewType.AgentReturn:
    case ViewType.Return:
      return clients.find((client) => client.returnClientKey === activeClient?.returnClientKey)
    default:
      return undefined
  }
}

const getSearchResult = async (): Promise<void> => {
  selected.value = undefined
  clientHits.value = []
  motionStore.items = []

  const query = searchStore.searchQuery

  if (query.length <= 3 || query.length > 1000) {
    warning('message.searchError')
    return
  }

  appStore.isIndeterminate = true

  try {
    clientHits.value = await searchStore.getSearchResult()

    searchStore.addRecentTerm(query)

    if (clientHits.value.length === 1 && hasCurrentClient.value) {
      isSearchMenuOpen.value = false
    } else {
      isSearchMenuOpen.value = true
    }

    const client = findSelectedClient(clientHits.value)

    if (client) {
      selected.value = client
    }

    showResult()
  } finally {
    appStore.isIndeterminate = false
  }
}

const clearResult = (): void => {
  searchStore.searchQuery = ''
}

const openResult = (): void => {
  isSearchMenuOpen.value = true
}

const getMotionSelectedItems = async (): Promise<void> => {
  scrollToTop()

  motionStore.priorityItems = clientHits.value.flatMap((client) =>
    client.items.map((item) => {
      return {
        id: item.mBoxId,
      }
    }),
  )

  filterStore.filter.status = [1, 2, 3, 4, 5, 6, 7]
  await motionStore.getMotionSelectedItems()
}

const checkSelectedClient = async (): Promise<void> => {
  if (!userStore.user) return
  if (!selected.value) return

  if (selected.value.clientKey !== userStore.user?.activeClient?.clientKey) {
    userStore.setActiveClient(selected.value)

    await appStore.isClientReady(route.name)
  }

  if (
    selected.value &&
    selected.value.returnClientKey === userStore.user.activeClient?.returnClientKey
  ) {
    const view = getReturnView()

    if (view) {
      clientStore.activeViewId = view.viewId

      await appStore.isViewReady(route.name)
    }
  } else {
    clientStore.activeViewId = clientStore.views[0].viewId

    await appStore.isViewReady(route.name)
  }
}

const getReturnView = (): ClientView | undefined =>
  clientStore.views.find(
    (view) => view.viewId === ViewType.AgentReturn || view.viewId === ViewType.Return,
  )

const checkCurrentRoute = async (): Promise<void> => {
  if (router.currentRoute.value.name === ROUTE_NAMES.MOTION_VIEW) return

  await router.push({ name: ROUTE_NAMES.MOTION_VIEW })
}

const showResult = async (): Promise<void> => {
  motionStore.items = []
  appStore.isInfoOpen = false
  clearAll()
  performSearch()
}

const clearAll = (): void => {
  filterStore.clearTag()
  filterStore.setSearchTag(searchStore.searchQuery)
}

const applyResult = async (): Promise<void> => {
  isSearchMenuOpen.value = false
  motionStore.items = []
  appStore.isInfoOpen = false
  performSearch()
}

watch(
  () => filterStore.tagContext,
  () => {
    if (isSearchDisabled.value) {
      isFocus.value = false
      isSearchMenuOpen.value = false
      selected.value = undefined
      clientHits.value = []
    }
  },
)
</script>

<template>
  <div class="relative">
    <SearchInput
      v-model="searchStore.searchQuery"
      v-model:open="isSearchMenuOpen"
      v-model:focus="isFocus"
      :title="$t('common.search')"
      @enter="getSearchResult"
      @clear="clearResult"
      @open="openResult"
    />
    <SearchMenu
      v-model="selected"
      v-model:open="isSearchMenuOpen"
      :active="!isSearchDisabled"
      :current="hasCurrentClient"
      :hits="clientHits"
      :loading="isLoading"
      @apply="applyResult"
      @click="getSearchResult"
    />
  </div>
</template>
